import { useEffect, useState } from 'react';
import tw from 'twin.macro';

import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { FilterControllerButton } from '@insights_module/components/episode_details_screen/FilterControllerButton';
import { CaretIconUp } from '@icons/CaretIconUp';
import { CaretIcon } from '@icons/CaretIcon';
import { CloseIcon } from '@icons/CloseIcon';
import {
  getElementsByContainingText,
  getSpanElementBySpanIndex,
  highlightMatchingSubstrings,
  removeMatchingSubstringHighlighting,
} from '@insights_module/utils/episodesUtils';
import { useEpisodeSegment } from '@insights_module/hooks/useEpisodeSegment';
import { SPAN_DATA_NAME } from '@insights_module/constants/filterControllers';

const Container = tw.div`max-w-[400px] flex flex-row items-center bg-neutral-background rounded-sm px-2 py-1 gap-1 cursor-pointer`;
const EntityLabel = tw.p`text-[11px] text-text-and-icon truncate`;
const EntityCount = tw.p`text-[11px] text-text-and-icon-muted`;
const CloseButton = tw.div`flex justify-center items-center w-[12px] h-[12px] cursor-pointer`;

const highlightedElementClassSet = ['rounded', 'bg-nif-highlight-2', 'p-0.5'];

export const EntitiesFilterController = () => {
  const { selectedEntity, setSelectedEntity, selectedSegment, setSelectedSegment } =
    useEpisodeDetailsContext();
  const { segmentsWithParagraphs } = useEpisodeSegment();
  const [selectedSpanIndex, setSelectedSpanIndex] = useState<number>(0);
  const [elements, setElements] = useState<HTMLElement[]>([]);

  const handlePrev = () => {
    if (!selectedEntity) return;
    removeHighlightedElementClassSet();

    if (selectedSpanIndex > 0) {
      setSelectedSpanIndex(selectedSpanIndex - 1);
      return;
    }

    if (selectedEntity.timestamps) {
      setSelectedSpanIndex(selectedEntity.timestamps.length - 1);
    }
  };

  const handleNext = () => {
    if (!selectedEntity || !selectedEntity?.timestamps) return;
    removeHighlightedElementClassSet();

    if (selectedSpanIndex < selectedEntity?.timestamps?.length - 1) {
      setSelectedSpanIndex(selectedSpanIndex + 1);
      return;
    }

    setSelectedSpanIndex(0);
  };

  const handleClose = () => {
    removeHighlightedElementClassSet();
    setSelectedEntity(null);
    setSelectedSpanIndex(0);
  };

  const scrollToEntity = () => {
    if (!selectedEntity) return;

    getSpanElementBySpanIndex(elements, selectedSpanIndex)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  useEffect(() => {
    const transcript = document.getElementById('transcript');
    if (!transcript) return;
    // in case if we had selected search term before selecting entity it could happen that we don't it
    // it it was wrapped in span, so we need to remove it
    removeMatchingSubstringHighlighting(transcript, SPAN_DATA_NAME.SELECTED_SEARCH_TERM);
    removeMatchingSubstringHighlighting(transcript, SPAN_DATA_NAME.SELECTED_ENTITY);

    if (!selectedEntity) return;

    const elements = getElementsByContainingText(transcript, selectedEntity.value);
    const filteredElements = elements.reduce((elements, element) => {
      const data = element.getAttribute('data-index');
      if (!data) return elements;

      const [segmentId, paragraphIndex] = data?.split('-') || [];
      if (!segmentId || !paragraphIndex) return elements;

      const paragraph =
        segmentsWithParagraphs[Number(segmentId)]?.paragraphData?.[Number(paragraphIndex)];
      if (!paragraph || !selectedEntity?.timestamps) return elements;

      selectedEntity?.timestamps.forEach((timestamp) => {
        if (
          timestamp[0] >= Number(paragraph.startTime) &&
          timestamp[1] <= Number(paragraph.endTime)
        ) {
          let hasMatchForStartTime = false;
          let hasMatchForEndTime = false;
          paragraph.paragraphWords.forEach((word) => {
            if (Number(word.start_time) === timestamp[0]) hasMatchForStartTime = true;
            if (Number(word.end_time) === timestamp[1]) hasMatchForEndTime = true;
          });

          if (hasMatchForStartTime && hasMatchForEndTime) {
            elements.push(element);
          }
        }
      });

      return elements;
    }, [] as HTMLElement[]);

    highlightMatchingSubstrings(
      filteredElements,
      selectedEntity.value,
      ['font-bold', 'bg-nif-highlight', 'p-0.5', 'rounded'],
      SPAN_DATA_NAME.SELECTED_ENTITY
    );

    setSelectedSpanIndex(0);
    setElements(filteredElements);
  }, [selectedEntity]);

  useEffect(() => {
    if (!elements.length) return;

    const span = getSpanElementBySpanIndex(elements, selectedSpanIndex);
    span?.classList.add(...highlightedElementClassSet);

    if (selectedSegment) {
      setSelectedSegment(null);
    }
    // need to wait for the DOM to update before scrolling
    const timeoutId = setTimeout(() => {
      span?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [selectedSpanIndex, elements]);

  const removeHighlightedElementClassSet = () => {
    if (!elements.length) return;

    const span = getSpanElementBySpanIndex(elements, selectedSpanIndex);
    span?.classList.remove(...highlightedElementClassSet);
  };

  if (!selectedEntity || !selectedEntity?.timestamps) return null;

  return (
    <Container>
      <EntityLabel onClick={scrollToEntity}>{selectedEntity.value}</EntityLabel>

      <EntityCount>{`${selectedSpanIndex + 1}/${selectedEntity?.timestamps.length}`}</EntityCount>

      <FilterControllerButton onClick={handlePrev}>
        <CaretIconUp tw='stroke-text-and-icon w-2' />
      </FilterControllerButton>
      <FilterControllerButton onClick={handleNext}>
        <CaretIcon tw='fill-text-and-icon w-2' />
      </FilterControllerButton>

      <CloseButton onClick={handleClose}>
        <CloseIcon tw='fill-text-and-icon w-[8px] h-[8px]' />
      </CloseButton>
    </Container>
  );
};
